import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import GenericSelect from '../../components/ui/GenericSelect';
import DataTable from '../../components/ui/DataTable';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import { requiresNoteHint } from './config';

const SelectWrap = styled.div`
  min-width: 320px;
  margin-bottom: 16px;
`;

const EmptyBox = styled.div`
  padding: 26px 18px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

const RangeHint = styled.span`
  font-size: 12px;
  color: #64748B;
`;

const InlineCellWrap = styled.div`
  min-width: 90px;
`;

const NoteWrap = styled.div`
  min-width: 160px;
`;

const NoteRequiredHint = styled.div`
  font-size: 11px;
  color: #EA580C;
  margin-top: 2px;
`;

const RowSaveBtn = styled(Button)`
  padding: 6px 10px !important;
`;

// Faz G (spec 2026-08-08 §3/§4, Task 4) — Ekibim sekmesi: yönetici self-servis
// ücret artış/prim önerisi girişi. CompRecommendationProposeRequest'in
// (proposedPct/proposedNote) BE'de BİRLİKTE değerlendirilmesi (aralık-dışıysa
// notsuz istek TAMAMEN reddedilir, task-3-report.md §4) — CycleDetailView'ın
// finalPct/hrNote'unun AKSİNE bu iki alan BAĞIMSIZ DEĞİL, bu yüzden inline
// otomatik-kaydet YERİNE satır başına AÇIK "Kaydet" (Gönder) butonu: kullanıcı
// pct+not'u birlikte düzenler, tek PUT ile birlikte gönderilir. Aralık-dışı+
// notsuz iken Kaydet DEVRE DIŞI (client-side ipucu — asıl kapı BE'de, brief).
export default function MyTeamTab({ cycles, proposeRecommendation }) {
  const { t } = useTranslation();
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [edits, setEdits] = useState({});
  const [savingRowId, setSavingRowId] = useState(null);

  useEffect(() => {
    if (!selectedCycleId && cycles.length > 0) setSelectedCycleId(cycles[0].cycleId);
  }, [cycles, selectedCycleId]);

  const selectedCycle = useMemo(
    () => cycles.find((c) => c.cycleId === selectedCycleId) || null,
    [cycles, selectedCycleId],
  );
  const rows = useMemo(() => selectedCycle?.rows || [], [selectedCycle]);

  const cycleOptions = useMemo(() => cycles.map((c) => ({
    id: c.cycleId,
    name: `${c.cycleName} (${t(`compCycles.type.${c.cycleType}`, { defaultValue: c.cycleType })})`,
  })), [cycles, t]);

  const getEdit = useCallback((row) => edits[row.id] || {
    pct: row.proposedPct ?? '',
    note: row.proposedNote ?? '',
  }, [edits]);

  const setEditField = useCallback((rowId, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [field]: value },
    }));
  }, []);

  const handleSaveRow = useCallback(async (row) => {
    const edit = getEdit(row);
    const pctNum = edit.pct === '' ? null : Number(edit.pct);
    setSavingRowId(row.id);
    const result = await proposeRecommendation(row.id, {
      proposedPct: pctNum,
      proposedNote: edit.note || null,
    });
    setSavingRowId(null);
    if (result.ok) {
      setEdits((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    }
  }, [getEdit, proposeRecommendation]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'employeeName', label: t('compCycles.fields.employee'), options: { filter: false, sort: false } },
    {
      name: 'departmentName',
      label: t('compCycles.fields.department'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'levelLabel',
      label: t('compCycles.fields.level'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'minPct',
      label: t('compCycles.fields.range'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          if (!row) return null;
          if (row.minPct == null || row.maxPct == null) return <RangeHint>{t('compCycles.myTeam.noRange')}</RangeHint>;
          return <RangeHint>{`${row.minPct}% / ${row.targetPct}% / ${row.maxPct}%`}</RangeHint>;
        },
      },
    },
    {
      name: 'proposedPct',
      label: t('compCycles.fields.proposedPct'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          if (!row) return null;
          const edit = getEdit(row);
          return (
            <InlineCellWrap>
              <TextInput
                type="number"
                min="0"
                max="100"
                placeholder="%"
                value={edit.pct}
                onChange={(e) => setEditField(row.id, 'pct', e.target.value)}
              />
            </InlineCellWrap>
          );
        },
      },
    },
    {
      name: 'proposedNote',
      label: t('compCycles.fields.proposedNote'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          if (!row) return null;
          const edit = getEdit(row);
          const needsNote = requiresNoteHint(edit.pct, row.minPct, row.maxPct);
          return (
            <NoteWrap>
              <TextInput
                value={edit.note}
                onChange={(e) => setEditField(row.id, 'note', e.target.value)}
                required={needsNote}
              />
              {needsNote && !edit.note?.trim() && (
                <NoteRequiredHint>{t('compCycles.myTeam.noteRequiredHint')}</NoteRequiredHint>
              )}
            </NoteWrap>
          );
        },
      },
    },
    {
      name: 'status',
      label: t('common.status'),
      options: {
        filter: true,
        sort: false,
        customBodyRender: (v) => t(`compCycles.recommendationStatus.${v}`, { defaultValue: v }),
      },
    },
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender: (_, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          if (!row) return null;
          const edit = getEdit(row);
          const needsNote = requiresNoteHint(edit.pct, row.minPct, row.maxPct);
          const blocked = edit.pct === '' || (needsNote && !edit.note?.trim());
          return (
            <RowSaveBtn
              data-role="propose-save-btn"
              onClick={() => handleSaveRow(row)}
              disabled={blocked || savingRowId === row.id}
              loading={savingRowId === row.id}
            >
              <Check size={14} />
              {t('common.save')}
            </RowSaveBtn>
          );
        },
      },
    },
  ], [t, rows, getEdit, setEditField, handleSaveRow, savingRowId]);

  if (cycles.length === 0) {
    return <EmptyBox>{t('compCycles.myTeam.noCycles')}</EmptyBox>;
  }

  return (
    <>
      <SelectWrap>
        <GenericSelect
          label={t('compCycles.myTeam.selectCycle')}
          value={selectedCycleId}
          onChange={setSelectedCycleId}
          options={cycleOptions}
          idKey="id"
          labelKey="name"
        />
      </SelectWrap>

      <DataTable
        title={t('compCycles.myTeam.title')}
        fetching={false}
        columns={columns}
        data={rows}
        serverSide={false}
        canCreateNewRecord={false}
      />
    </>
  );
}
